import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getChatbotResponse } from '../../../services/openaiService';

const AIChatPopup = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Здравствуйте! Я AI-ассистент Jetu AI. Готов ответить на ваши вопросы о разработке сайтов и приложений. Чем могу помочь?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const conversationHistoryRef = useRef([]);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!input?.trim() || isLoading) return;

    const userMessage = input?.trim();
    setInput('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const assistantMessage = await getChatbotResponse(userMessage, conversationHistoryRef.current);
      
      // Обновляем историю разговора
      conversationHistoryRef.current = [
        ...conversationHistoryRef.current,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: assistantMessage }
      ];
      
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Извините, произошла ошибка при обработке вашего сообщения. Пожалуйста, попробуйте еще раз или свяжитесь с нами напрямую.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:justify-end md:p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full md:w-[400px] h-[100vh] md:h-[600px] bg-card border border-border md:rounded-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom md:slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-accent/10 to-primary/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
              <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">AI Ассистент Jetu</h3>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                <span className="text-xs text-muted-foreground">Онлайн</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted transition-colors duration-200 flex items-center justify-center"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Messages */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/20">
          {messages?.map((message, index) => (
            <div
              key={index}
              className={`flex ${message?.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                  message?.role === 'user' ?'bg-accent text-accent-foreground rounded-br-none' :'bg-card border border-border rounded-bl-none'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message?.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] px-4 py-3 rounded-2xl bg-card border border-border rounded-bl-none">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-background">
          <div className="flex items-end space-x-2">
            <div className="flex-1">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Напишите ваш вопрос..."
                value={input}
                onChange={(e) => setInput(e?.target?.value)}
                disabled={isLoading}
              />
            </div>
            <Button
              type="submit"
              disabled={!input?.trim() || isLoading}
              className="h-[42px] w-[42px] p-0 flex items-center justify-center"
            >
              <Icon name="Send" size={18} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Демо-версия AI чата для вопросов о разработке
          </p>
        </form>
      </div>
    </div>
  );
};

export default AIChatPopup;